import { z } from "zod";
import { blueprintDimensionSchema } from "./exam-config";
import { itemTypeSchema } from "./items";
import { pricingConfigSchema } from "./pricing";
import { referenceDataSchema } from "./seed/contracts";
import { programmeCodeSchema } from "./programmes";

/**
 * Admin panel contracts (spec §7.2 Admin, §10.3). The role → capability matrix below is the single source for both
 * the server (every /admin/* call checks it, with an MFA session) and the console (which views a role sees).
 */
export const ADMIN_ROLES = ["owner", "moderator", "support", "finance", "content_reviewer"] as const;
export const adminRoleSchema = z.enum(ADMIN_ROLES);
export type AdminRole = z.infer<typeof adminRoleSchema>;

export const ADMIN_CAPABILITIES = [
  "dashboard",
  "users.view",
  "users.support_actions",
  "users.owner_actions",
  "moderation",
  "jobs.view",
  "jobs.retry",
  "revenue.view",
  "revenue.actions",
  "pricing.view",
  "pricing.edit",
  "programmes",
  "exam_formats",
  "clinical_qa",
  "support",
  "content",
  "settings",
  "team",
  "audit_log",
] as const;
export type AdminCapability = (typeof ADMIN_CAPABILITIES)[number];

/** Owner decision 2026-10-18 (§10.3). Users: support actions = extend trial + resend emails; owner actions = comp Pro, suspend/reactivate, delete. */
export const ROLE_CAPABILITIES: Record<AdminRole, readonly AdminCapability[]> = {
  owner: ADMIN_CAPABILITIES,
  moderator: ["dashboard", "users.view", "moderation", "jobs.view", "jobs.retry"],
  support: ["dashboard", "users.view", "users.support_actions", "jobs.view", "support", "content"],
  finance: ["dashboard", "users.view", "revenue.view", "pricing.view", "audit_log"],
  /** Plan §7.3: checks exam formats against official sources; a second person must approve before "verified". */
  content_reviewer: ["dashboard", "exam_formats", "clinical_qa"],
};

export function can(role: AdminRole | null | undefined, capability: AdminCapability): boolean {
  return !!role && ROLE_CAPABILITIES[role].includes(capability);
}

export const ROLE_LABELS: Record<AdminRole, string> = {
  owner: "Owner",
  moderator: "Moderator",
  support: "Support",
  finance: "Finance (read-only)",
  content_reviewer: "Content reviewer",
};

const uuid = z.string().uuid();
const iso = z.string();
const money = z.number();
const cursorList = <T extends z.ZodTypeAny>(item: T) => z.object({ items: z.array(item), next_cursor: z.string().nullable() });

// ---------------------------------------------------------------- me
export const adminMeSchema = z.object({
  user_id: uuid,
  email: z.string().nullable(),
  display_name: z.string(),
  role: adminRoleSchema,
  capabilities: z.array(z.enum(ADMIN_CAPABILITIES)),
});
export type AdminMe = z.infer<typeof adminMeSchema>;

// ---------------------------------------------------------------- dashboard (#1)
export const adminActivitySchema = z.object({
  event: z.string(),
  created_at: iso,
  surface: z.string(),
  user_name: z.string().nullable(),
  properties: z.record(z.string(), z.unknown()),
});
export const adminDashboardSchema = z.object({
  total_users: z.number().int(),
  signups_30d: z.number().int(),
  signups_prev_30d: z.number().int(),
  pro_active: z.number().int(),
  trials: z.number().int(),
  mrr_usd: money,
  sets_7d: z.number().int(),
  sets_prev_7d: z.number().int(),
  open_flags: z.number().int(),
  oldest_open_flag: iso.nullable(),
  jobs_failed: z.number().int(),
  jobs_active: z.number().int(),
  open_tickets: z.number().int(),
  ai_cost_30d_usd: money,
  ai_calls_30d: z.number().int(),
  tutor_questions_30d: z.number().int(),
  limit_hits_30d: z.number().int(),
  programme_split: z.array(z.object({ code: z.string(), name: z.string(), exam_label: z.string(), users: z.number().int() })),
  weekly_signups: z.array(z.object({ week: z.string(), signups: z.number().int() })),
  activity: z.array(adminActivitySchema),
});
export type AdminDashboard = z.infer<typeof adminDashboardSchema>;

/** Sidebar badges: open queue sizes, visible to the roles that can act on them. */
export const adminCountsSchema = z.object({ open_flags: z.number().int().nullable(), failed_jobs: z.number().int().nullable(), open_tickets: z.number().int().nullable() });
export type AdminCounts = z.infer<typeof adminCountsSchema>;

// ---------------------------------------------------------------- users (#2)
export const ADMIN_USER_PLANS = ["free", "trial", "pro"] as const;
export const ADMIN_USER_STATUSES = ["active", "suspended", "deletion_pending"] as const;
export const adminUserQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  programme: programmeCodeSchema.optional(),
  plan: z.enum(ADMIN_USER_PLANS).optional(),
  status: z.enum(ADMIN_USER_STATUSES).optional(),
  cursor: z.string().max(200).optional(),
});
export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>;

export const adminUserSchema = z.object({
  id: uuid,
  email: z.string().nullable(),
  display_name: z.string(),
  programme: z.string().nullable(),
  exam: z.string().nullable(),
  plan: z.enum(ADMIN_USER_PLANS),
  entitlement_status: z.string().nullable(),
  sets: z.number().int(),
  last_active: iso.nullable(),
  status: z.enum(ADMIN_USER_STATUSES),
  created_at: iso,
});
export type AdminUser = z.infer<typeof adminUserSchema>;
export const adminUserListSchema = cursorList(adminUserSchema);

export const adminUserDetailSchema = adminUserSchema.extend({
  timezone: z.string(),
  exam_date: z.string().nullable(),
  school: z.string().nullable(),
  xp_total: z.number().int(),
  level: z.number().int(),
  email_confirmed: z.boolean(),
  suspended_at: iso.nullable(),
  deletion_requested_at: iso.nullable(),
  entitlement: z
    .object({ status: z.string(), source: z.string(), product_id: z.string(), current_period_end: iso.nullable(), store: z.string().nullable(), will_renew: z.boolean() })
    .nullable(),
  usage: z.object({ period: z.string(), sets_generated: z.number().int(), voice_minutes: z.number().int(), quick_mocks: z.number().int(), diagrams: z.number().int() }),
  open_flags: z.number().int(),
  tickets: z.number().int(),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

export const extendTrialRequestSchema = z.object({ days: z.number().int().min(1).max(90) });
export const compProRequestSchema = z.object({ until: z.string().date().nullable(), note: z.string().trim().min(1).max(300) });
export const suspendRequestSchema = z.object({ reason: z.string().trim().min(1).max(300) });
export const resendEmailRequestSchema = z.object({ kind: z.enum(["confirmation", "password_reset"]) });
export const okSchema = z.object({ ok: z.literal(true) });

// ---------------------------------------------------------------- moderation (#3)
export const MODERATION_ACTIONS = ["approve", "edit", "remove"] as const;
export const moderationActionSchema = z.enum(MODERATION_ACTIONS);
export type ModerationAction = z.infer<typeof moderationActionSchema>;

export const moderationItemSchema = z.object({
  flag_id: uuid,
  reason: z.string(),
  note: z.string().nullable(),
  created_at: iso,
  reporter_name: z.string().nullable(),
  set: z.object({ id: uuid, title: z.string(), programme: z.string(), exam: z.string() }).nullable(),
  item_kind: z.enum(["question", "card"]),
  item: z
    .object({
      id: uuid,
      status: z.string(),
      item_type: itemTypeSchema.nullable(),
      /** Question payload, or { front, back } for a card. */
      content: z.record(z.string(), z.unknown()),
    })
    .nullable(),
  citations: z.array(z.object({ id: uuid, anchor: z.string(), text: z.string(), source_filename: z.string() })),
});
export type ModerationItem = z.infer<typeof moderationItemSchema>;
export const moderationQueueSchema = z.object({
  items: z.array(moderationItemSchema),
  stats: z.object({ open: z.number().int(), resolved_today: z.number().int(), avg_resolution_seconds: z.number().nullable() }),
});
export type ModerationQueue = z.infer<typeof moderationQueueSchema>;

/** Edits a moderator may make: the stem, the correct-answer rationale and option texts (questions), or both card sides. */
export const moderationEditSchema = z.union([
  z.object({
    kind: z.literal("question"),
    stem: z.string().trim().min(1).max(4000),
    rationale: z.string().trim().min(1).max(4000),
    options: z.array(z.object({ id: z.string().min(1).max(40), text: z.string().trim().min(1).max(1000) })).max(10).optional(),
  }),
  z.object({ kind: z.literal("card"), front: z.string().trim().min(1).max(400), back: z.string().trim().min(1).max(800) }),
]);
export type ModerationEdit = z.infer<typeof moderationEditSchema>;
export const moderationActionRequestSchema = z.object({ edit: moderationEditSchema.optional(), note: z.string().trim().max(500).optional() });

// ---------------------------------------------------------------- jobs (#4)
export const ADMIN_JOB_STATUSES = ["queued", "running", "succeeded", "failed"] as const;
export const adminJobQuerySchema = z.object({
  status: z.enum(ADMIN_JOB_STATUSES).optional(),
  tier: z.enum(["free", "paid"]).optional(),
  cursor: z.string().max(200).optional(),
});
export const adminJobSchema = z.object({
  id: uuid,
  kind: z.string(),
  status: z.enum(ADMIN_JOB_STATUSES),
  stage: z.string(),
  ai_tier: z.enum(["free", "paid"]),
  attempts: z.number().int(),
  max_attempts: z.number().int(),
  user_name: z.string().nullable(),
  set_title: z.string().nullable(),
  programme: z.string().nullable(),
  source_label: z.string().nullable(),
  last_error: z.string().nullable(),
  user_error: z.string().nullable(),
  tokens_in: z.number(),
  tokens_out: z.number(),
  cost_usd: money,
  duration_seconds: z.number().nullable(),
  not_before: iso,
  created_at: iso,
  updated_at: iso,
});
export type AdminJob = z.infer<typeof adminJobSchema>;
export const adminJobListSchema = cursorList(adminJobSchema);
export const adminJobStatsSchema = z.object({
  daily: z.array(z.object({ day: z.string(), ready: z.number().int(), failed: z.number().int() })),
  by_status: z.record(z.string(), z.number().int()),
  cost_30d_by_tier: z.record(z.string(), z.number()),
  calls_24h_by_tier: z.record(z.string(), z.number().int()),
  failures_24h_by_tier: z.record(z.string(), z.number().int()),
  median_seconds_7d: z.number().nullable(),
  free_queue: z.object({ waiting: z.number().int(), oldest: iso.nullable() }),
  tiers: z.array(z.object({ tier: z.string(), blocked_until: iso.nullable() })),
  providers: z.array(z.object({ provider: z.string(), model: z.string().default(""), tier: z.string(), blocked_until: iso.nullable(), last_error: z.string().nullable() })),
  /** Prompt-cache reuse per model, last 24 hours (spec v1.7 §6.7). */
  cache_24h: z.array(z.object({ provider: z.string(), model: z.string(), tokens_in: z.number(), tokens_cached: z.number(), calls: z.number() })).default([]),
});
export type AdminJobStats = z.infer<typeof adminJobStatsSchema>;

// ---------------------------------------------------------------- revenue (#5)
export const adminTransactionSchema = z.object({
  id: uuid,
  user_name: z.string().nullable(),
  type: z.string(),
  product: z.string().nullable(),
  store: z.string().nullable(),
  amount_usd: z.number().nullable(),
  environment: z.string().nullable(),
  received_at: iso,
});
export type AdminTransaction = z.infer<typeof adminTransactionSchema>;
export const adminRevenueSchema = z.object({
  plans: z.array(z.object({ key: z.string(), name: z.string(), display_price_usd: money, billing_period: z.string(), subscribers: z.number().int() })),
  free_users: z.number().int(),
  mrr_usd: money,
  arr_usd: money,
  trials: z.number().int(),
  billing_issues: z.number().int(),
  churn_30d_percent: z.number().nullable(),
  transactions: cursorList(adminTransactionSchema),
});
export type AdminRevenue = z.infer<typeof adminRevenueSchema>;
export const recordRefundRequestSchema = z.object({ outcome: z.enum(["refunded", "declined"]), note: z.string().trim().min(1).max(300) });

// ---------------------------------------------------------------- pricing (#10)
export const adminPricingSchema = z.object({
  current: pricingConfigSchema,
  history: z.array(z.object({ version: z.number().int(), published_at: iso.nullable(), published_by: z.string().nullable(), summary: z.array(z.string()) })),
});
export type AdminPricing = z.infer<typeof adminPricingSchema>;
export const DIVERGENCE_STATUSES = ["in_sync", "diverged", "not_configured", "unavailable"] as const;
/** Where students are charged for Pro (§9). App Store and Google Play use store price points; web uses checkout. */
export const STORES = ["app_store", "google_play", "web"] as const;
export type Store = (typeof STORES)[number];
export const STORE_LABELS: Record<Store, string> = { app_store: "App Store", google_play: "Google Play", web: "Web checkout" };
const paidPlanKey = z.enum(["pro_monthly", "pro_yearly"]);
export const storePriceSchema = z.object({
  plan_key: paidPlanKey,
  store: z.enum(STORES),
  price_usd: z.number().nonnegative(),
  /** "manual" = entered in Admin; "automatic" is reserved for reading App Store / Google Play later (v1.7). */
  source: z.enum(["manual", "automatic"]),
  checked_at: iso,
});
export type StorePrice = z.infer<typeof storePriceSchema>;
/** Save the hand-entered store prices; null clears an entry. */
export const saveStorePricesSchema = z.object({
  entries: z.array(z.object({ plan_key: paidPlanKey, store: z.enum(STORES), price_usd: z.number().nonnegative().max(1000).nullable() })).min(1).max(6),
});
export type SaveStorePrices = z.infer<typeof saveStorePricesSchema>;
export const pricingDivergenceSchema = z.object({
  status: z.enum(DIVERGENCE_STATUSES),
  checked_at: iso,
  plans: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      display_price_usd: money,
      store_prices: z.array(z.object({ store: z.enum(STORES), price_usd: z.number().nullable(), source: z.enum(["manual", "automatic"]).nullable(), checked_at: iso.nullable() })),
      diverged: z.boolean(),
    }),
  ),
  message: z.string(),
});
export type PricingDivergence = z.infer<typeof pricingDivergenceSchema>;

const usd = (n: number) => `$${n.toFixed(2)}`;

/**
 * Store vs Admin price check (§9): every paid plan's display price against each store price entered for it. Any
 * mismatch → diverged; all stores entered and matching → in_sync; otherwise not_configured, naming what's missing.
 */
export function compareStorePrices(
  plans: Array<{ key: string; name: string; display_price_usd: number }>,
  entries: StorePrice[],
  now = new Date(),
): PricingDivergence {
  const paid = plans.filter((p) => p.key !== "free");
  const rows = paid.map((p) => {
    const store_prices = STORES.map((store) => {
      const e = entries.find((x) => x.plan_key === p.key && x.store === store);
      return { store, price_usd: e?.price_usd ?? null, source: e?.source ?? null, checked_at: e?.checked_at ?? null };
    });
    const diverged = store_prices.some((s) => s.price_usd !== null && Math.abs(s.price_usd - p.display_price_usd) >= 0.005);
    return { key: p.key, name: p.name, display_price_usd: p.display_price_usd, store_prices, diverged };
  });
  const mismatches = rows.flatMap((r) =>
    r.store_prices
      .filter((s) => s.price_usd !== null && Math.abs(s.price_usd - r.display_price_usd) >= 0.005)
      .map((s) => `${r.name} is ${usd(r.display_price_usd)} here but ${usd(s.price_usd!)} on ${STORE_LABELS[s.store]}`),
  );
  const missing = rows.flatMap((r) => r.store_prices.filter((s) => s.price_usd === null).map((s) => `${r.name} on ${STORE_LABELS[s.store]}`));
  const status = mismatches.length ? "diverged" : missing.length ? "not_configured" : "in_sync";
  const message =
    status === "diverged"
      ? `${mismatches.join("; ")}. Match the store price or change the display price before students see it.`
      : status === "in_sync"
        ? "Every store price entered matches the display price."
        : `No store price entered yet for ${missing.join(", ")}. Enter what each store charges to check it.`;
  return { status, checked_at: now.toISOString(), plans: rows, message };
}

// ---------------------------------------------------------------- programmes (#6)
export const adminProgrammeSchema = z.object({
  code: programmeCodeSchema,
  name: z.string(),
  exam_label: z.string(),
  active: z.boolean(),
  users: z.number().int(),
  configs: z.array(
    z.object({
      id: uuid,
      exam: z.string(),
      exam_name: z.string(),
      config_version: z.string(),
      style_pack_version: z.string(),
      effective_from: z.string(),
      effective_to: z.string().nullable(),
      in_force: z.boolean(),
      blueprint: z.array(blueprintDimensionSchema),
      difficulty_mix: z.object({ easy: z.number(), medium: z.number(), hard: z.number() }),
      questions_per_set: z.number().int(),
      cards_per_set: z.number().int(),
      item_types: z.array(z.string()),
    }),
  ),
  style_packs: z.array(z.string()),
});
export type AdminProgramme = z.infer<typeof adminProgrammeSchema>;
export const adminProgrammesSchema = z.object({ items: z.array(adminProgrammeSchema) });
export const programmeUpdateSchema = z.object({ active: z.boolean() });
/**
 * Edits to the config in force (Admin #6): category names and weights per dimension (keys are fixed — items are
 * tagged with them), style-pack version, set sizes and difficulty mix. Validated again by examConfigSchema.
 */
export const examConfigUpdateSchema = z
  .object({
    blueprint: z.array(blueprintDimensionSchema).min(1).max(2),
    style_pack_version: z.string().min(1).max(40),
    questions_per_set: z.number().int().min(5).max(60),
    cards_per_set: z.number().int().min(5).max(80),
    difficulty_mix: z.object({ easy: z.number().int().min(0).max(100), medium: z.number().int().min(0).max(100), hard: z.number().int().min(0).max(100) }),
  })
  .superRefine((v, ctx) => {
    if (v.difficulty_mix.easy + v.difficulty_mix.medium + v.difficulty_mix.hard !== 100) {
      ctx.addIssue({ code: "custom", message: "Difficulty mix must add up to 100%.", path: ["difficulty_mix"] });
    }
  });
export type ExamConfigUpdate = z.infer<typeof examConfigUpdateSchema>;

// ---------------------------------------------------------------- usability funnel (plan BE1, §11 KPIs)
const count = z.coerce.number().int().nonnegative();
export const adminFunnelSchema = z.object({
  days: z.number().int(),
  onboarding: z.array(z.object({ step: z.string(), viewed: count, completed: count })),
  activation: z.object({ signed_up: count, onboarded: count, uploaded: count, generated: count, studied: count, returned_in_week: count }),
  /** stops = how often this was the last screen someone saw that day. */
  screens: z.array(z.object({ screen: z.string().nullable(), views: count, actors: count, stops: count })),
  early_exits: z.array(z.object({ mode: z.string().nullable(), exits: count, avg_answered: z.coerce.number().nullable() })),
});
export type AdminFunnel = z.infer<typeof adminFunnelSchema>;
export const adminFunnelQuerySchema = z.object({ days: z.coerce.number().int().min(1).max(365).default(30) });

// ---------------------------------------------------------------- exam format verification (plan §7.3–7.4)
export const VERIFICATION_STATUSES = ["draft", "in_review", "verified", "stale"] as const;
export const verificationStatusSchema = z.enum(VERIFICATION_STATUSES);
export type VerificationStatus = z.infer<typeof verificationStatusSchema>;
export const FORMAT_ALERT_KINDS = ["source_changed", "source_unreachable", "plan_ending", "passing_standard_ending", "review_due"] as const;
/** Two different reviewers must approve the same config version before it is verified (enforced in the database too). */
export const REQUIRED_APPROVALS = 2;
/** A verified config is re-checked at least this often, sooner if its plan ends first. */
export const REVIEW_INTERVAL_MONTHS = 6;

export const formatSourceSchema = z.object({
  id: uuid,
  url: z.string().url(),
  label: z.string(),
  page_ref: z.string().nullable(),
  sha256: z.string().nullable(),
  retrieved_at: iso.nullable(),
  last_checked_at: iso.nullable(),
  last_change_at: iso.nullable(),
});
export const formatReviewSchema = z.object({
  id: uuid,
  config_version: z.string(),
  reviewer_name: z.string().nullable(),
  decision: z.enum(["approve", "request_changes"]),
  notes: z.string(),
  created_at: iso,
});
export const formatAlertSchema = z.object({
  id: uuid,
  exam_config_id: uuid,
  exam_name: z.string(),
  effective_from: z.string(),
  kind: z.enum(FORMAT_ALERT_KINDS),
  detail: z.string(),
  created_at: iso,
});
export type FormatAlert = z.infer<typeof formatAlertSchema>;
export const formatAlertListSchema = z.object({ items: z.array(formatAlertSchema) });
export const formatVerificationSchema = z.object({
  exam_config_id: uuid,
  exam_name: z.string(),
  config_version: z.string(),
  effective_from: z.string(),
  effective_to: z.string().nullable(),
  status: verificationStatusSchema,
  verified_at: iso.nullable(),
  next_review_due: z.string().nullable(),
  approvals_for_current_version: z.number().int(),
  sources: z.array(formatSourceSchema),
  reviews: z.array(formatReviewSchema),
  alerts: z.array(formatAlertSchema),
});
export type FormatVerification = z.infer<typeof formatVerificationSchema>;
/** Official sources only (exam body or test vendor), always https. */
export const formatSourceRequestSchema = z.object({
  url: z.string().trim().url().max(500).refine((u) => u.startsWith("https://"), "Use the https:// address of the official page."),
  label: z.string().trim().min(1).max(200),
  page_ref: z.string().trim().max(100).optional(),
});
export const formatReviewRequestSchema = z.object({ decision: z.enum(["approve", "request_changes"]), notes: z.string().trim().max(2000).default("") });

// ---------------------------------------------------------------- support (#7) — shared with the student endpoints
export const TICKET_STATUSES = ["open", "in_progress", "resolved"] as const;
export const TICKET_PRIORITIES = ["low", "medium", "high"] as const;
export const supportMessageSchema = z.object({ id: uuid, from_staff: z.boolean(), internal: z.boolean(), author_name: z.string().nullable(), body: z.string(), created_at: iso });
export type SupportMessage = z.infer<typeof supportMessageSchema>;
export const supportTicketSchema = z.object({
  id: uuid,
  number: z.number().int(),
  subject: z.string(),
  status: z.enum(TICKET_STATUSES),
  priority: z.enum(TICKET_PRIORITIES),
  channel: z.string(),
  last_message_at: iso,
  created_at: iso,
});
export type SupportTicket = z.infer<typeof supportTicketSchema>;
export const supportTicketListSchema = z.object({ items: z.array(supportTicketSchema) });
export const supportThreadSchema = supportTicketSchema.extend({ messages: z.array(supportMessageSchema) });
export type SupportThread = z.infer<typeof supportThreadSchema>;
export const createTicketRequestSchema = z.object({ subject: z.string().trim().min(3).max(120), body: z.string().trim().min(10).max(4000) });
export const ticketMessageRequestSchema = z.object({ body: z.string().trim().min(1).max(4000) });

export const adminTicketSchema = supportTicketSchema.extend({
  user_id: uuid,
  user_name: z.string().nullable(),
  programme: z.string().nullable(),
  assignee_id: uuid.nullable(),
  assignee_name: z.string().nullable(),
});
export type AdminTicket = z.infer<typeof adminTicketSchema>;
export const adminTicketListSchema = z.object({ items: z.array(adminTicketSchema), open: z.number().int() });
export const adminTicketThreadSchema = adminTicketSchema.extend({ messages: z.array(supportMessageSchema) });
export type AdminTicketThread = z.infer<typeof adminTicketThreadSchema>;
export const adminTicketReplySchema = z.object({ body: z.string().trim().min(1).max(4000), internal: z.boolean().default(false) });
export const adminTicketUpdateSchema = z.object({
  status: z.enum(TICKET_STATUSES).optional(),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  assignee_id: uuid.nullable().optional(),
});

// ---------------------------------------------------------------- announcements (#8)
export const ANNOUNCEMENT_AUDIENCES = ["all", "free", "pro", "programme"] as const;
export const ANNOUNCEMENT_SURFACES = ["app", "web"] as const;
export const announcementWriteSchema = z
  .object({
    message: z.string().trim().min(1).max(200),
    audience: z.enum(ANNOUNCEMENT_AUDIENCES),
    programme: programmeCodeSchema.nullable(),
    surfaces: z.array(z.enum(ANNOUNCEMENT_SURFACES)).min(1),
    published: z.boolean(),
    starts_at: z.string().datetime({ offset: true }).nullable(),
    ends_at: z.string().datetime({ offset: true }).nullable(),
  })
  .superRefine((v, ctx) => {
    if ((v.audience === "programme") !== (v.programme !== null)) ctx.addIssue({ code: "custom", message: "Pick a programme for a programme banner.", path: ["programme"] });
    if (v.starts_at && v.ends_at && Date.parse(v.ends_at) <= Date.parse(v.starts_at)) ctx.addIssue({ code: "custom", message: "The end must be after the start.", path: ["ends_at"] });
  });
export type AnnouncementWrite = z.infer<typeof announcementWriteSchema>;
export const adminAnnouncementSchema = z.object({
  id: uuid,
  message: z.string(),
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  programme: z.string().nullable(),
  surfaces: z.array(z.enum(ANNOUNCEMENT_SURFACES)),
  published: z.boolean(),
  starts_at: iso.nullable(),
  ends_at: iso.nullable(),
  created_at: iso,
  updated_at: iso,
});
export type AdminAnnouncement = z.infer<typeof adminAnnouncementSchema>;
export const adminAnnouncementListSchema = z.object({ items: z.array(adminAnnouncementSchema) });

/** What students get from GET /announcements/active: the newest live banner for their plan, programme and surface. */
export const activeAnnouncementSchema = z.object({ announcement: z.object({ id: uuid, message: z.string() }).nullable() });
export type ActiveAnnouncement = z.infer<typeof activeAnnouncementSchema>;

// ---------------------------------------------------------------- settings & system (#9)
export const adminFeatureFlagSchema = z.object({ key: z.string(), enabled: z.boolean(), description: z.string(), updated_at: iso });
export const adminSettingsSchema = z.object({
  flags: z.array(adminFeatureFlagSchema),
  /** One row per model slot and tier (spec v1.6 §6.1): which jobs it serves, whether its key is set, and its state. */
  ai: z.array(z.object({ slot: z.string(), provider: z.string(), model: z.string(), jobs: z.array(z.string()), tier: z.enum(["free", "paid"]), configured: z.boolean(), calls_24h: z.number().int(), failures_24h: z.number().int(), blocked_until: iso.nullable() })),
  system: z.object({ environment: z.string(), pricing_version: z.number().int(), revenuecat_configured: z.boolean(), email_configured: z.boolean(), pro_private_ai: z.boolean().default(false) }),
});
export type AdminSettings = z.infer<typeof adminSettingsSchema>;
export const featureFlagUpdateSchema = z.object({ enabled: z.boolean() });

export const adminTeamMemberSchema = z.object({
  user_id: uuid,
  email: z.string().nullable(),
  display_name: z.string(),
  role: adminRoleSchema,
  mfa_enrolled: z.boolean(),
  last_sign_in_at: iso.nullable(),
  invited: z.boolean(),
});
export type AdminTeamMember = z.infer<typeof adminTeamMemberSchema>;
export const adminTeamSchema = z.object({ items: z.array(adminTeamMemberSchema) });
export const teamInviteRequestSchema = z.object({ email: z.string().trim().toLowerCase().email().max(254), role: adminRoleSchema });
export const teamUpdateRequestSchema = z.object({ role: adminRoleSchema });

// ---------------------------------------------------------------- audit log
export const auditEntrySchema = z.object({
  id: uuid,
  actor_name: z.string().nullable(),
  action: z.string(),
  target_table: z.string(),
  target_id: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  created_at: iso,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export const auditLogSchema = cursorList(auditEntrySchema);
export const auditQuerySchema = z.object({ action: z.string().max(60).optional(), cursor: z.string().max(200).optional() });

/** Keyset cursor for created_at-ordered lists: "<iso>|<uuid>". */
export function encodeCursor(createdAt: string, id: string): string {
  return `${createdAt}|${id}`;
}
export function decodeCursor(cursor: string | undefined): { createdAt: string; id: string } | null {
  if (!cursor) return null;
  const [createdAt, id] = cursor.split("|");
  if (!createdAt || !id || Number.isNaN(Date.parse(createdAt)) || !uuid.safeParse(id).success) return null;
  return { createdAt, id };
}

// ---------------------------------------------------------------- reference data (#6, §13.4b–d)
export const adminReferenceSchema = z.object({
  items: z.array(
    z.object({
      kind: z.enum(["lab_values", "dosage_rules", "prioritisation_frameworks", "disallowed_content"]),
      current: referenceDataSchema.nullable(),
      versions: z.array(z.object({ version: z.string(), created_at: iso })),
    }),
  ),
});
export type AdminReference = z.infer<typeof adminReferenceSchema>;
/** Publishing a kind's items creates the next version; generation uses it from the next stage on. */
export const referencePublishSchema = z.object({ items: z.array(z.unknown()).min(1).max(200) });
